import { formList, useForm, yupResolver } from "@mantine/form";
import React, { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { AxiosError } from "axios";
import { UseMutateFunction } from "react-query";
import { AuthContext } from "../../../providers/AuthProvider";
import { HttpClientError } from "../../../providers/HttpClient";
import EmployeeUploadFile from "../elements/EmployeeUploadFile";
import EmployeeUploadFormValidationSchema from "../services/validator/EmployeeUploadFormValidationSchema";
import EmployeeImportMatchColumnPanel, {
  rowsData,
} from "./EmployeeImportMatchColumnPanel";
import EmployeeImportConfigPanel from "./EmployeeImportConfigPanel";
import EmployeeUploadApiRequest from "../services/request/EmployeeUploadApiRequest";
import EmployeeUploadApiResponse from "../services/model/EmployeeUploadApiResponse";
import StepperTabs, {
  StepperTab,
} from "../../../themes/components/StepperTabs";
import { useToasts } from "../../../providers/notifications/ToastsContext";

type EmployeeImportFormProps = {
  activeTab: number;
  onTabChange: React.Dispatch<React.SetStateAction<number>>;
  onFileUpload: React.Dispatch<React.SetStateAction<boolean>>;
  isError: boolean;
  error?: AxiosError | null;
  isSuccess: boolean;
  mutate: UseMutateFunction<
    EmployeeUploadApiResponse,
    AxiosError,
    EmployeeUploadApiRequest
  >;
  onSuccess: () => void;
};

const EmployeeImportForm: React.FC<EmployeeImportFormProps> = ({
  onFileUpload,
  activeTab,
  onTabChange,
  isError,
  error,
  isSuccess,
  mutate,
  onSuccess,
}) => {
  const auth = useContext(AuthContext);
  const { showToast } = useToasts();

  const [dataExtract, setDataExtract] = useState<rowsData>();
  const [formValidation, setFormValidation] = useState<boolean>(false);

  const form = useForm({
    schema: yupResolver(EmployeeUploadFormValidationSchema),
    initialValues: {
      file: undefined,
      name: "",
      configurationName: "Default",
      configurationToUse: "",
      columnHeaders: formList([
        {
          name: "",
          relatedColumn: "",
          isTag: false,
        },
      ]),
      importAction: "",
      lookupHeader: "",
      useExistingConfiguration: false,
    },
  });

  const onSubmitHandler = (formValues: typeof form.values): void => {
    let configName = "Default";

    if (formValues.file) {
      if (
        formValues.configurationName &&
        formValues.configurationName.length > 0
      ) {
        configName = formValues.configurationName;
      }

      mutate({
        file: formValues.file,
        lookup_header: formValues.lookupHeader,
        configuration_is_customized: false.toString(),
        overwrite_configuration: false.toString(),
        delete_missing: Object.is(
          formValues.importAction,
          "create_update_delete"
        ).toString(),
        use_existing_configuration:
          formValues.useExistingConfiguration.toString(),
        employer_data_import_configuration_name: formValues.name,
        employer: auth?.employer.id.toString() ?? "",
        custom_configuration: JSON.stringify(
          formValues.columnHeaders.map((it) => ({
            name: configName,
            csv_field: it.name,
            smartwage_field: it.relatedColumn,
            is_tag: it.isTag,
            ignore_in_import: !it.isTag && !it.relatedColumn,
          }))
        ),
      });
    }
  };

  useEffect(() => {
    if (activeTab === 3) {
      form.validate();
      setFormValidation(!formValidation);
    }
  }, [formValidation]);

  useEffect(() => {
    if (activeTab === 3) {
      if (
        form.errors &&
        Object.keys(form.errors).length > 0 &&
        Object.getPrototypeOf(form.errors) === Object.prototype
      ) {
        onTabChange(2);
      } else {
        onSubmitHandler(form.values);
      }
    }
  }, [form.values, activeTab]);

  useEffect(() => {
    if (isError && error) {
      showToast({
        strapiId: "employee_data_import_failed",
        data: { message: HttpClientError(error) },
      });
    }
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess) {
      showToast({ strapiId: "employee_data_import_success" });
      onTabChange(0);
      onSuccess();
    }
  }, [isSuccess]);

  const nextStep = (): void => {
    if (form.values.file) {
      onTabChange((current: number) => (current < 3 ? current + 1 : current));
    }
  };

  const onUploadSuccess = (data: rowsData): void => {
    setDataExtract(data);
    onFileUpload(true);
  };

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  const StepperTabsList = [
    {
      id: "upload-file",
      title: "Upload file",
      content: (
        <EmployeeUploadFile auth={auth} form={form} onDone={onUploadSuccess} />
      ),
    },
    {
      id: "map-columns",
      title: "Map columns",
      content: (
        <EmployeeImportMatchColumnPanel form={form} dataExtract={dataExtract} />
      ),
    },
    {
      id: "confirm-and-process",
      title: "Confirm and process",
      content: <EmployeeImportConfigPanel auth={auth} form={form} />,
    },
  ] as StepperTab[];

  return (
    <section
      style={{
        padding: "0px 40px",
        backgroundColor: "#ffffff",
      }}
    >
      <StepperTabs
        activeIndex={activeTab}
        indexCallback={nextStep}
        tabsList={StepperTabsList}
      />
    </section>
  );
};

export default EmployeeImportForm;
